import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { getAuditLogs, getAuditLog } from './api';
import { AuditLog, PaginatedResponse } from '../../shared/types';

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<PaginatedResponse<AuditLog> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadLogs = useCallback(async (page: number) => {
    setIsLoading(true);
    try {
      const response = await getAuditLogs({ page, per_page: 20 });
      setLogs(response);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs(currentPage);
  }, [currentPage, loadLogs]);

  const openDetailModal = async (id: number) => {
    try {
      const log = await getAuditLog(id);
      setSelectedLog(log);
      setIsDetailModalOpen(true);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    }
  };

  const getActionBadge = (action: string) => {
    if (action.includes('create')) return 'badge-success';
    if (action.includes('delete')) return 'badge-danger';
    if (action.includes('update') || action.includes('activate') || action.includes('deactivate')) return 'badge-warning';
    return 'badge-info';
  };

  const formatJson = (data: Record<string, unknown> | null) => {
    if (!data) return null;
    return JSON.stringify(data, null, 2);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Logs</h1>
          <p className="text-slate-400 mt-1">Track all administrative actions</p>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">Action</th>
                      <th className="table-header">Entity</th>
                      <th className="table-header">Admin</th>
                      <th className="table-header">IP Address</th>
                      <th className="table-header">Date</th>
                      <th className="table-header text-right">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {logs?.data.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell">
                          <span className={`badge ${getActionBadge(log.action)}`}>
                            {log.action.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className="text-white capitalize">{log.entity_type}</span>
                          {log.entity_id && (
                            <span className="text-slate-500 ml-1">#{log.entity_id}</span>
                          )}
                        </td>
                        <td className="table-cell">
                          {log.admin ? (
                            <div>
                              <p className="text-white font-medium">{log.admin.name}</p>
                              <p className="text-xs text-slate-500">{log.admin.email}</p>
                            </div>
                          ) : (
                            <span className="text-slate-500">Unknown</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className="font-mono text-xs text-slate-400">
                            {log.ip_address || '-'}
                          </span>
                        </td>
                        <td className="table-cell">
                          <div>
                            <p className="text-white">
                              {new Date(log.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(log.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </td>
                        <td className="table-cell text-right">
                          <button
                            onClick={() => openDetailModal(log.id)}
                            className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {logs && logs.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={logs.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Audit Log Details"
      >
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Action</p>
                <span className={`badge ${getActionBadge(selectedLog.action)}`}>
                  {selectedLog.action.replace(/_/g, ' ')}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Entity</p>
                <p className="text-white capitalize">
                  {selectedLog.entity_type}
                  {selectedLog.entity_id && ` #${selectedLog.entity_id}`}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Admin</p>
                <p className="text-white">{selectedLog.admin?.name || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">IP Address</p>
                <p className="text-white font-mono text-sm">{selectedLog.ip_address || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Date</p>
                <p className="text-white">
                  {new Date(selectedLog.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {selectedLog.old_values && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Old Values</p>
                <pre className="bg-slate-800 rounded-lg p-3 text-xs text-slate-300 overflow-auto max-h-32 font-mono">
                  {formatJson(selectedLog.old_values)}
                </pre>
              </div>
            )}

            {selectedLog.new_values && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">New Values</p>
                <pre className="bg-slate-800 rounded-lg p-3 text-xs text-slate-300 overflow-auto max-h-32 font-mono">
                  {formatJson(selectedLog.new_values)}
                </pre>
              </div>
            )}

            {selectedLog.user_agent && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">User Agent</p>
                <p className="text-xs text-slate-400 break-all">{selectedLog.user_agent}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default AuditLogs;
